import { signProfileAsset } from "@/lib/profileAssetSigning.mjs";
// src/components/nav/DesktopHeaderTabs.jsx
import React from "react";
import { createPortal } from "react-dom";
import { MessageSquareWarning } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext.jsx";
import supabase from "@/services/supabaseClient";
import { logout } from "@/lib/auth/logout";
import NavAlertBadge from "@/components/nav/NavAlertBadge.jsx";
import AfrimedSearchBar from "@/components/ui/AfrimedSearchBar.jsx";
import useUnreadMessages from "@/hooks/useUnreadMessages.js";
import useUnreadNotifications from "@/hooks/useUnreadNotifications.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import ContextFeedbackLauncher from "@/components/feedback/ContextFeedbackLauncher.jsx";
import {
  clearAvatarSessionCache,
  readAvatarSessionCache,
  writeAvatarSessionCache,
} from "@/lib/avatarSessionCache.js";
import { resolveMessagesTabTarget } from "@/utils/messagesNavigationState.js";

// Header icons
const ICON_ELEPHANT = "/icons/afrimed-icon@512.svg";
const ICON_BELL = "/icons/appointments/dashboard/notifications.svg";
const DASHBOARD = "/icons/appointments/dashboard/dashboard.svg";
const HELP = "/icons/appointments/dashboard/help.svg";
const PROFILE = "/icons/doctor/appointments/account_circle.svg";
const SETTINGS = "/icons/appointments/dashboard/settings.svg";
const EPRESCRIPTIONS = "/icons/appointments/dashboard/e-prescriptions.svg";
const AVAILABILITY = "/icons/list-items/schedule_Fill.svg";
const SIGN_OUT = "/icons/appointments/dashboard/log-out.svg";
const LOG_OUT_CRITICAL = "/icons/appointments/dashboard/log-out-critical.svg";

const DOCTOR_AVATAR_BUCKET = "doctor-profile-images";
const PATIENT_AVATAR_BUCKET = "patient-profile-images";

function canUseDevLocalStorage() {
  try {
    const host = window.location.hostname;
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host.endsWith(".local");
    return !!import.meta.env.DEV && isLocal;
  } catch {
    return false;
  }
}

const DEV_DOCTOR_PHOTO_KEY = "afrimed:dev:doctor_profile_photo";
const DEV_PATIENT_PHOTO_KEY = "afrimed:dev:patient_profile_photo";

function safeGetLocalStorage(key) {
  try {
    return window?.localStorage?.getItem(key) || "";
  } catch {
    return "";
  }
}

// ✅ robust role getter (works in bypass + normal auth)
function getRole(auth) {
  const profile = auth?.profile || null;
  const metaRole =
    auth?.session?.user?.user_metadata?.role ||
    auth?.user?.user_metadata?.role ||
    "";

  const role = profile?.role || auth?.role || metaRole || "";
  return String(role).toLowerCase();
}

function clsChip(active) {
  return `
    h-9 px-1.5 lg:h-10 lg:px-2
    flex items-center justify-center
    text-[13px] lg:text-body-medium
    border-b-2
    ${active
      ? "text-on-surface border-[rgba(var(--clr-on-surface),0.88)]"
      : "border-transparent text-on-surface-var hover:text-on-surface"}
  `;
}

function MenuItem({ label, icon, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="
        w-full text-left px-4 py-3
        flex items-center gap-3
        text-body-medium text-on-surface
        hover:bg-[rgba(24,50,119,0.06)]
        disabled:opacity-60 disabled:cursor-not-allowed
      "
    >
      {typeof icon === "function" ? icon() : icon ? <img src={icon} alt="" className="h-5 w-5" draggable="false" /> : null}
      <span className="flex-1">{label}</span>
    </button>
  );
}

function FeedbackNavIcon() {
  return (
    <MessageSquareWarning
      className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant))]"
      aria-hidden
    />
  );
}

export default function DesktopHeaderTabs() {
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const searchLabel = t("navigation.search", "Search");
  const globalSearchLabel = t("navigation.globalSearch", "Global search");
  const meLabel = t("navigation.me", "Me");

  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const profile = auth.profile || null;

  const role = getRole(auth);
  const isDoctor = role === "doctor";
  const isPracticeTeam = role === "practice_team";
  const roleTable = isDoctor ? "doctor_profiles" : "patient_profiles";
  const searchTargetRoute = isDoctor ? "/doctors/appointments" : "/appointments/doctors";
  const helpRoute = "/help";

  const [open, setOpen] = React.useState(false);
  const meMenuRef = React.useRef(null);
  const meMenuPanelRef = React.useRef(null);
  const [meMenuPosition, setMeMenuPosition] = React.useState({ top: 0, right: 12 });
  React.useEffect(() => setOpen(false), [location.pathname]);

  React.useEffect(() => {
    if (!open) return undefined;

    function positionMenu() {
      const trigger = meMenuRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      setMeMenuPosition({
        top: Math.round(rect.bottom + 8),
        right: Math.max(12, Math.round(window.innerWidth - rect.right)),
      });
    }

    function handlePointerDown(event) {
      if (meMenuRef.current?.contains(event.target)) return;
      if (meMenuPanelRef.current?.contains(event.target)) return;
      setOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    positionMenu();
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open]);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [ePrescriptionsBusy, setEPrescriptionsBusy] = React.useState(false);
  const { unreadCount } = useUnreadNotifications(auth?.user?.id);
  const { unreadCount: unreadMessages } = useUnreadMessages(auth?.user?.id);

  // Logout dialog
  const [logoutOpen, setLogoutOpen] = React.useState(false);
  const [logoutPending, setLogoutPending] = React.useState(false);

  // ✅ DB truth (your staging columns):
  // - profile_photo_path (path in storage bucket)  ✅ preferred
  // - profile_image_url  (full URL)                ✅ fallback
  const authProfilePhotoPath = String(
    profile?.profile_photo_path ||
      profile?.profile_photo_storage_path ||
      profile?.photo_path ||
      profile?.avatar_path ||
      ""
  ).trim();
  const authProfileImageUrl = String(
    profile?.profile_image_url || profile?.avatar_url || ""
  ).trim();
  const cachedAvatarSrc = React.useMemo(
    () => readAvatarSessionCache({ role, userId: auth?.user?.id }),
    [auth?.user?.id, role]
  );
  const [avatarSrc, setAvatarSrc] = React.useState(
    () => cachedAvatarSrc || authProfileImageUrl || ""
  );
  const [rolePhotoPath, setRolePhotoPath] = React.useState(
    () => authProfilePhotoPath
  );
  const [rolePhotoUrl, setRolePhotoUrl] = React.useState(
    () => authProfileImageUrl
  );

  const profilePhotoPath = rolePhotoPath || authProfilePhotoPath;
  const profileImageUrl = rolePhotoUrl || authProfileImageUrl;
  // Keep the visible "Me" avatar independent from the background role probe.
  // This makes a route transition paint the existing session-cache URL first.
  const visibleAvatarSrc = cachedAvatarSrc || avatarSrc || authProfileImageUrl || "";

  React.useEffect(() => {
    let alive = true;

    async function hydrateRoleAvatar() {
      if (!auth?.user?.id) {
        if (alive) {
          setRolePhotoPath("");
          setRolePhotoUrl("");
        }
        return;
      }

      try {
        const { data, error } = await supabase
          .from(roleTable)
          .select("*")
          .eq("user_id", auth.user.id)
          .maybeSingle();

        if (!alive) return;
        if (error || !data) {
          if (!authProfilePhotoPath && !authProfileImageUrl) {
            setRolePhotoPath("");
            setRolePhotoUrl("");
            clearAvatarSessionCache({ role, userId: auth.user.id });
          }
          return;
        }
        const nextPath = String(
          data.profile_photo_path ||
            data.profile_photo_storage_path ||
            data.photo_path ||
            data.avatar_path ||
            ""
        ).trim();
        const nextUrl = String(
          data.profile_image_url ||
            data.profile_photo_url ||
            data.photo_url ||
            data.avatar_url ||
            ""
        ).trim();
        setRolePhotoPath(nextPath);
        setRolePhotoUrl(nextUrl);
        if (!nextPath && !nextUrl && !authProfilePhotoPath && !authProfileImageUrl) {
          clearAvatarSessionCache({ role, userId: auth.user.id });
        }
      } catch {
        if (!alive) return;
        if (!authProfilePhotoPath && !authProfileImageUrl) {
          setRolePhotoPath("");
          setRolePhotoUrl("");
          clearAvatarSessionCache({ role, userId: auth.user.id });
        }
      }
    }

    hydrateRoleAvatar();
    return () => {
      alive = false;
    };
  }, [auth?.user?.id, authProfileImageUrl, authProfilePhotoPath, roleTable]);

  React.useEffect(() => {
    let alive = true;

    async function resolveAvatar() {
      let next = cachedAvatarSrc || profileImageUrl || "";

      if (next && alive) {
        setAvatarSrc(next);
      }

      const avatarBucket = isDoctor ? DOCTOR_AVATAR_BUCKET : PATIENT_AVATAR_BUCKET;

      // 1) Prefer fresh signing from storage path (prevents expired signed-url issues).
      if (profilePhotoPath) {
        try {
          const storage = supabase.storage.from(avatarBucket);
          const { data, error } = await signProfileAsset(supabase, avatarBucket, profilePhotoPath);

          if (!error && data?.signedUrl) {
            next = data.signedUrl;
          } else {
            const { data: publicData } = storage.getPublicUrl(profilePhotoPath);
            next = publicData?.publicUrl || "";
          }
        } catch {
          // ignore
        }
      }

      // 2) Fallback to URL fields if signing/path did not resolve.
      if (!next && profileImageUrl && /^https?:\/\//i.test(profileImageUrl)) {
        next = profileImageUrl;
      }

      // 3) Dev override ONLY on localhost dev
      if (canUseDevLocalStorage()) {
        const devKey = isDoctor ? DEV_DOCTOR_PHOTO_KEY : DEV_PATIENT_PHOTO_KEY;
        const devPhoto = safeGetLocalStorage(devKey);
        if (devPhoto) next = devPhoto;
      }

      if (!alive) return;
      const finalSrc = next || "";
      setAvatarSrc(finalSrc);
      if (finalSrc) {
        writeAvatarSessionCache({ role, userId: auth?.user?.id, src: finalSrc });
      }
    }

    resolveAvatar();
    return () => {
      alive = false;
    };
  }, [auth?.user?.id, cachedAvatarSrc, isDoctor, profileImageUrl, profilePhotoPath, role]);

  const appointmentsRoute = isDoctor ? "/doctors/appointments" : "/appointments";
  const messagesRoute = resolveMessagesTabTarget(auth?.user?.id, location.pathname);
  const appointmentsMatch = (path) =>
    isDoctor
      ? path === "/doctors/appointments" || path.startsWith("/doctors/appointments/")
      : path === "/appointments" || path.startsWith("/appointments/");

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = String(params.get("q") || params.get("search") || "").trim();
    setSearchValue(q);
  }, [location.search]);

  function submitGlobalSearch(event) {
    event?.preventDefault?.();
    const q = String(searchValue || "").trim();
    if (!q) {
      nav(searchTargetRoute);
      return;
    }
    nav(`${searchTargetRoute}?q=${encodeURIComponent(q)}`);
  }

  function clearGlobalSearch() {
    setSearchValue("");
    nav(searchTargetRoute);
  }

  // ✅ Tabs: Dashboard is doctor-only
  const TABS = [
    ...(isDoctor
      ? [
          {
            to: "/home/doctor",
            label: t("navigation.tabs.dashboard", "Dashboard"),
            match: (p) =>
              p === "/home/doctor" ||
              p.startsWith("/home/doctor/") ||
              p === "/doctor/dashboard" ||
              p.startsWith("/doctor/dashboard/"),
          },
        ]
      : []),
    {
      to: appointmentsRoute,
      label: t("navigation.tabs.appointments", "Appointments"),
      match: appointmentsMatch,
    },
    {
      to: messagesRoute,
      label: t("navigation.tabs.messages", "Messages"),
      badgeCount: unreadMessages,
      match: (p) => p === "/messages" || p.startsWith("/messages/"),
    },
  ];

  // ✅ Elephant routes to HOME by role
  const homeRoute = isPracticeTeam ? "/practice-team/welcome" : isDoctor ? "/home/doctor" : "/home/patient";
  const profileRoute = isPracticeTeam ? "/practice-team/welcome" : isDoctor ? "/doctor/profile" : "/patient/profile";
  const settingsRoute = isPracticeTeam ? "/practice-team/welcome" : isDoctor ? "/settings/doctor" : "/settings/patient";
  const notificationsRoute = isDoctor
    ? "/settings/doctor/notifications"
    : isPracticeTeam
      ? "/practice-team/welcome"
      : "/settings/patient/notifications";

async function doLogout() {
  if (logoutPending) return;

  try {
    setLogoutPending(true);

    if (canUseDevLocalStorage()) {
      try {
        localStorage.removeItem(DEV_DOCTOR_PHOTO_KEY);
      } catch {}
    }

    await logout({ redirectTo: "/login?logout=1" });
  } catch (e) {
    console.error("Logout failed:", e);
    window.location.href = "/login?logout=1";
  } finally {
    setLogoutPending(false);
  }
}

function isActiveClinicalRoute(pathname = "") {
  const path = String(pathname || "").toLowerCase();
  return path.includes("/call/") || path.includes("/video/");
}

function leaveFeedbackBeforeLogout() {
  const returnTo = `${location.pathname}${location.search || ""}${location.hash || ""}`;
  setLogoutOpen(false);
  nav(`/feedback?intent=signout&from=${encodeURIComponent(returnTo)}`);
}

async function openEPrescriptions() {
  if (ePrescriptionsBusy) return;

  setOpen(false);
  setEPrescriptionsBusy(true);

  try {
    const userId = auth?.user?.id || "";

    if (!userId) {
      nav(isDoctor ? "/doctors/appointments" : "/appointments");
      return;
    }

    if (isDoctor) {
      nav("/doctors/prescriptions", {
        state: { returnTo: location.pathname || "/doctor/dashboard" },
      });
      return;
    }

    const { data: latestPrescription, error } = await supabase
      .from("prescriptions")
      .select("id, issued_at, created_at")
      .eq("patient_user_id", userId)
      .order("issued_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!error && latestPrescription?.id) {
      nav(`/appointments/prescriptions/${latestPrescription.id}`);
      return;
    }

    nav("/appointments");
  } catch {
    nav(isDoctor ? "/doctors/appointments" : "/appointments");
  } finally {
    setEPrescriptionsBusy(false);
  }
}

  return (
    <>
      <div className="hidden md:block sticky top-0 z-40 w-full border-b border-[rgba(15,23,42,0.06)] bg-[rgb(var(--clr-surface-cont-highest))]">
        <div className="afrimed-rail afrimed-rail--comfortable">
          <div className="flex h-14 items-center gap-4 lg:gap-8 xl:gap-12">
            <button
              type="button"
              onClick={() => nav(homeRoute)}
              aria-label="AfriMed"
              title="AfriMed™"
            >
              <img src={ICON_ELEPHANT} alt="" className="h-8 w-8 lg:h-9 lg:w-9" draggable="false" />
            </button>

            <div className="flex min-w-0 flex-1 items-center">
              <div className="flex min-w-0 items-center gap-2 lg:gap-4 xl:gap-6">
                {TABS.map((t) => {
                  const active = typeof t.match === "function"
                    ? t.match(location.pathname)
                    : location.pathname === t.to || location.pathname.startsWith(`${t.to}/`);
                  return (
                    <NavLink key={t.label} to={t.to} className={() => clsChip(active)}>
                      <span className="inline-flex items-center gap-1.5">
                        <span>{t.label}</span>
                        {Number(t?.badgeCount || 0) > 0 ? (
                          <NavAlertBadge count={t.badgeCount} className="shrink-0" />
                        ) : null}
                      </span>
                    </NavLink>
                  );
                })}
              </div>

              <form
                className="ml-auto hidden md:block w-full max-w-[170px] lg:max-w-[220px] xl:max-w-[248px]"
                onSubmit={submitGlobalSearch}
                role="search"
                aria-label={globalSearchLabel}
              >
                <button type="submit" className="sr-only">
                  {searchLabel}
                </button>
                <AfrimedSearchBar
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onClear={clearGlobalSearch}
                  placeholder={searchLabel}
                  ariaLabel={globalSearchLabel}
                  className="bg-[rgba(86,106,139,0.10)]"
                  inputClassName="text-[13px] lg:text-label-medium"
                  showTrailing={false}
                />
              </form>
            </div>

            <div className="relative ml-auto flex items-center gap-1 lg:gap-2">
              <button
                type="button"
                onClick={() => nav(notificationsRoute)}
                className="relative grid h-8 w-8 place-items-center rounded-xl hover:bg-[rgba(24,50,119,0.06)] lg:h-9 lg:w-9"
                aria-label={t("notifications.common.title", "Notifications")}
                title={t("notifications.common.title", "Notifications")}
              >
                <img src={ICON_BELL} alt="" className="h-5 w-5 lg:h-6 lg:w-6" draggable="false" />
                {unreadCount > 1 ? (
                  <span
                    className="
                      absolute -right-1 -top-1
                      min-w-[18px] h-[18px] px-1
                      rounded-full
                      bg-[rgb(var(--schemes-critical,248,83,58))]
                      text-[10px] leading-[18px] text-white text-center font-medium
                    "
                  >
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                ) : unreadCount === 1 ? (
                  <span
                    className="
                      absolute right-[7px] top-[7px]
                      h-2.5 w-2.5 rounded-full
                      bg-[rgb(var(--schemes-critical,248,83,58))]
                    "
                  />
                ) : null}
              </button>

              <button
                type="button"
                onClick={() => nav(helpRoute)}
                className="grid h-8 w-8 place-items-center rounded-xl hover:bg-[rgba(24,50,119,0.06)] lg:h-9 lg:w-9"
                aria-label="Help"
                title="Help"
              >
                <img src={HELP} alt="" className="h-5 w-5 lg:h-6 lg:w-6" draggable="false" />
              </button>

              <div className="relative" ref={meMenuRef}>
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  className="flex h-11 flex-col items-center justify-center rounded-[12px] px-1.5 hover:bg-[rgba(24,50,119,0.06)] lg:h-12 lg:px-2"
                  aria-label="Me menu"
                >
                  {visibleAvatarSrc ? (
                    <img
                      src={visibleAvatarSrc}
                      alt=""
                      className="h-6 w-6 rounded-full object-cover lg:h-7 lg:w-7"
                      draggable="false"
                    />
                  ) : (
                    <img
                      src="/icons/circular-avatar-placeholder.svg"
                      alt=""
                      className="h-6 w-6 rounded-full object-cover lg:h-7 lg:w-7"
                      draggable="false"
                    />
                  )}

                  <div className="mt-0.5 flex items-center gap-1 text-[10px] text-on-surface-var lg:text-label-small">
                    <span>{meLabel}</span>
                    <img
                      src={"/icons/appointments/dashboard/Show_less.svg"}
                      alt=""
                      className={`h-5 w-5 ${open ? "rotate-180" : ""}`}
                      draggable="false"
                    />
                  </div>
                </button>

                {open &&
                  typeof document !== "undefined" &&
                  createPortal(
                    <div
                      ref={meMenuPanelRef}
                      className="
                        fixed z-[200] w-[200px]
                        rounded-[12px]
                        bg-[rgb(var(--clr-surface-cont-highest))]
                        border border-[rgba(15,23,42,0.08)]
                        shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)]
                        overflow-hidden
                      "
                      style={{ top: meMenuPosition.top, right: meMenuPosition.right }}
                    >
                    <MenuItem
                      label={t("navigation.menu.viewProfile", "View profile")}
                      icon={PROFILE}
                      onClick={() => nav(profileRoute)}
                    />
                    {isDoctor ? (
                      <MenuItem
                        label={t("navigation.menu.dashboard", "Dashboard")}
                        icon={DASHBOARD}
                        onClick={() => nav("/doctor/dashboard")}
                      />
                    ) : null}
                    <MenuItem
                      label={t("navigation.menu.ePrescriptions", "E-Prescriptions")}
                      icon={EPRESCRIPTIONS}
                      onClick={openEPrescriptions}
                      disabled={ePrescriptionsBusy}
                    />
                    {isDoctor ? (
                      <MenuItem
                        label={t("navigation.menu.availability", "Availability")}
                        icon={AVAILABILITY}
                        onClick={() => nav("/doctor/p/availability")}
                      />
                    ) : null}
                    <MenuItem
                      label={t("navigation.menu.settings", "Settings")}
                      icon={SETTINGS}
                      onClick={() => nav(settingsRoute)}
                    />
                    <MenuItem
                      label={t("feedback.trigger", "Make suggestion")}
                      icon={FeedbackNavIcon}
                      onClick={() => {
                        setOpen(false);
                        nav(`/feedback?from=${encodeURIComponent(location.pathname)}`);
                      }}
                    />
                    <div className="h-px bg-[rgba(15,23,42,0.06)]" />
                    <MenuItem
                      label={t("navigation.menu.signOut", "Sign out")}
                      icon={SIGN_OUT}
                      onClick={() => {
                        setOpen(false);
                        if (isActiveClinicalRoute(location.pathname)) {
                          void doLogout();
                          return;
                        }
                        setLogoutOpen(true);
                      }}
                    />
                    </div>,
                    document.body,
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContextFeedbackLauncher
        section="header"
        context={{ surface: "desktop-header" }}
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        hideTrigger
      />

      {logoutOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[220] bg-[rgba(15,23,42,0.35)] backdrop-blur-sm flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            onClick={() => (logoutPending ? null : setLogoutOpen(false))}
          >
            <div
              className="w-[420px] rounded-2xl bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_18px_45px_rgba(15,23,42,0.18)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="mx-auto w-full max-w-[320px] flex items-start justify-center gap-3">
                  <img src={LOG_OUT_CRITICAL} alt="" className="h-6 w-6 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-title-medium text-on-surface">
                      {t("navigation.menu.logoutConfirmTitle", "Before you go, would you like to leave quick feedback?")}
                    </div>
                    <div className="mt-1 text-body-medium text-on-surface-var">
                      {t("navigation.menu.logoutConfirmBody", "It is optional, but staging notes help us improve AfriMed™ before production.")}
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  <button
                    type="button"
                    className="rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                    style={{
                      background: "var(--State-Layers-Primary-Opacity-08,rgba(29,79,215,0.08))",
                      color: "var(--Schemes-Primary,#1D4FD7)",
                    }}
                    disabled={logoutPending}
                    onClick={leaveFeedbackBeforeLogout}
                  >
                    {t("feedback.logout.leaveFeedback", "Leave feedback")}
                  </button>

                  <button
                    type="button"
                    className="rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                    style={{
                      background: "var(--State-Layers-Critical-Opacity-08,rgba(248,83,58,0.08))",
                      color: "var(--Schemes-Critical,#F8533A)",
                    }}
                    disabled={logoutPending}
                    onClick={doLogout}
                  >
                    {logoutPending
                      ? t("navigation.menu.loggingOut", "Logging out…")
                      : t("navigation.menu.logOut", "Log out")}
                  </button>

                  <button
                    type="button"
                    className="rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                    style={{
                      background: "rgba(24,50,119,0.06)",
                      color: "rgb(var(--clr-on-surface-variant))",
                    }}
                    disabled={logoutPending}
                    onClick={() => setLogoutOpen(false)}
                  >
                    {t("common.cancel", "Cancel")}
                  </button>
                </div>
              </div>

              <div className="h-px bg-[rgba(15,23,42,0.06)]" />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
